/**
 * Your own Torn history, kept on the Omni-Torn server (owner's call 2026-11-02 18:44: "Everything anyone requests should be
 * cached and stored on server. Additionally when they are offline and not using their api we should use it to grab their
 * older history so that its ready for them when they visit that page in the website"). From terms 19 (OWN_HISTORY_TERMS):
 *  - every read of a player's own streams (the log, all categories; their fights; their races) made by the website, the
 *    extension, Torn PDA or background tracking goes to the server for them, with the stretch of time it read in full;
 *  - clients ask the server first and read from Torn only what it doesn't have (newer than its newest, or a gap);
 *  - while the player isn't using Omni-Torn, the server walks their kept key back through older history (apps/api
 *    backfill.ts) until Torn has nothing older;
 *  - kept until "Delete my data" — never deleted by size (OWN_CAPS only stop the backfill and uploads) —, shown only to that player.
 * Below 19 the older limits stay (log 90 days, fights a year) and nothing a client reads is kept.
 * The `items` stream (website roadmap S39, item-stats.ts): a snapshot of each of your weapons and armor per read (its usage
 * stats, bonuses, first owner…) — the present state only, so there is no older history to backfill.
 * Pure helpers shared by the server (apps/api own-history.ts) and the clients.
 */
import { cleanItemSnap } from './item-stats';
import { mergeSpans, type Span } from './log-kit';
import type { KeyNeed } from './web';

/** The terms version from which a player's own history is kept on the server with no end date (and backfilled). */
export const OWN_HISTORY_TERMS = 19;

export type OwnStream = 'log' | 'attacks' | 'races' | 'items';
export const OWN_STREAMS: readonly OwnStream[] = ['log', 'attacks', 'races', 'items'];
/** The streams with older history Torn can still give (the server's backfill walks these); `items` is only ever "now". */
export const OWN_BACKFILL_STREAMS: readonly OwnStream[] = ['log', 'attacks', 'races'];

/** What a key needs to read each stream (Torn's selections): the log is Full only. */
export const OWN_STREAM_NEEDS: Readonly<Record<OwnStream, KeyNeed>> = {
  log: { level: 4, user: ['log'] },
  attacks: { level: 3, user: ['attacks'] },
  races: { level: 2 },
  items: { level: 2 },
};

/** Plain names (status lines). */
export const OWN_STREAM_NAMES: Readonly<Record<OwnStream, string>> = { log: 'activity log', attacks: 'fights', races: 'races', items: 'weapon and armor stats' };

/**
 * Per player and stream, how far back the server reads: once a stream holds this many (or the player's whole copy
 * OWN_BYTES_CAP bytes), the backfill stops reading older history and older uploads aren't taken — newer ones always are,
 * and nothing kept is ever deleted (owner: "We keep everything until they choose to delete it"). Sizes as kept (Torn's own
 * JSON): a log line ≈ 300–450 bytes, a fight ≈ 500, a race ≈ 1.5 KB, an item snapshot ≈ 500. Owner (2026-11-02): "Get rid of the 200MB cap. At
 * the very least make it like 2.5GB per player" — so OWN_BYTES_CAP is 2.5 GB of data (≈ 3.5 GB on disk with SQLite's ids
 * and indexes) and the counts are set high enough that the bytes bind first; most players far less (a year of an active
 * player's log ≈ 40 000 lines ≈ 16 MB).
 */
export const OWN_CAPS: Readonly<Record<OwnStream, number>> = { log: 7_000_000, attacks: 5_000_000, races: 1_700_000, items: 5_000_000 };

/** One upload: at most this many entries (Torn's pages are 100; a client may send what it read of a few pages). */
export const OWN_UPLOAD_MAX = 1_000;
/** Body size of one upload. */
export const OWN_UPLOAD_BYTES = 900_000;
/** One kept entry bigger than this is kept as a stub (when and what). */
export const OWN_ENTRY_BYTES = 4_000;

/** Not used for this long (no request from the extension, website or Torn PDA): the server may backfill with their key. */
export const BACKFILL_IDLE_MS = 15 * 60_000;
/** Backfill's share of the server's per-key budget (SERVER_CALLS_PER_KEY = 20, all uses together). */
export const BACKFILL_CALLS_PER_KEY = 5;

/**
 * Requests made because of a person — a page they're viewing or something they did — carry this header (the website while
 * its tab is visible; the extension and Torn PDA while a Torn page or the extension's own page was in use in the last
 * 90 s). Only these count as "using Omni-Torn" for the idle backfill; the clients' own timers don't.
 */
export const OWN_USE_HEADER = 'X-Omni-Torn-Use';

/** The server's name for a stream ("log", "log:cat:136", "attacks", "races"). */
export function ownStreamKey(stream: OwnStream, cat?: number | null): string {
  return stream === 'log' && cat && Number.isInteger(cat) && cat > 0 ? `log:cat:${cat}` : stream;
}

export function isOwnStream(s: unknown): s is OwnStream {
  return s === 'log' || s === 'attacks' || s === 'races' || s === 'items';
}

/** When an entry happened (ms), from Torn's own shape; null when it can't be told. */
export function ownEntryTime(stream: OwnStream, raw: unknown): number | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const e = raw as Record<string, unknown>;
  const sched = e.schedule && typeof e.schedule === 'object' ? (e.schedule as Record<string, unknown>) : {};
  const t = Number(stream === 'log' || stream === 'items' ? e.timestamp : stream === 'attacks' ? (e.timestamp_started ?? e.started ?? e.timestamp_ended ?? e.ended) : (sched.start ?? sched.end));
  if (!Number.isFinite(t) || t <= 0) return null;
  return t < 1e12 ? t * 1000 : t;
}

/** Is this fight one of the player's own (they attacked or defended)? Torn's v1 shape (a stealthed attacker is ""). */
function ownFight(e: Record<string, unknown>, playerId: number): boolean {
  return Number(e.attacker_id) === playerId || Number(e.defender_id) === playerId;
}

const isNum = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v);
const isIdish = (v: unknown): boolean => (typeof v === 'number' && Number.isInteger(v) && v >= 0) || v === '' || (typeof v === 'string' && /^\d{1,12}$/.test(v));

/**
 * Is this entry in Torn's own shape for the stream (what Torn's answer for that selection holds)? The log (v1 `log`): a
 * numeric `log` type, a `title`, a `timestamp`; a fight (v1 `attacks`): `timestamp_started`, attacker and defender ids,
 * a `result`; a race (v2 `user/races`): a numeric `id`, `track_id`, `schedule.start`; an item snapshot (item-stats.ts
 * `ItemSnap`, made from Torn's item answers): what `cleanItemSnap` keeps.
 */
export function tornShaped(stream: OwnStream, e: Record<string, unknown>): boolean {
  if (stream === 'items') return cleanItemSnap(e, Number.MAX_SAFE_INTEGER) !== null;
  if (stream === 'log') return isNum(e.log) && typeof e.title === 'string' && isNum(e.timestamp) && (e.data === undefined || (typeof e.data === 'object' && e.data !== null) || Array.isArray(e.data));
  if (stream === 'attacks') return isNum(e.timestamp_started) && isIdish(e.attacker_id) && isIdish(e.defender_id) && typeof e.result === 'string';
  const sched = e.schedule && typeof e.schedule === 'object' ? (e.schedule as Record<string, unknown>) : null;
  return isNum(e.id) && isNum(e.track_id) && !!sched && isNum(sched.start);
}

/** Per player, the most bytes (Torn's JSON as kept, all streams together) before the server stops reading older history. */
export const OWN_BYTES_CAP = 2.5 * 1024 * 1024 * 1024;

export interface OwnUpload {
  stream: OwnStream;
  /** Log category (v1 `cat`) when the read was one category. */
  cat: number | null;
  /** id → Torn's entry, as Torn gave it. */
  entries: Record<string, Record<string, unknown>>;
  /** The stretch the read covered in full (ms), when it says so. */
  span: Span | null;
}

/**
 * An upload as the server takes it, or why not. Only shapes Torn gives for the player's own selections: fights must name
 * the player on one side; every entry needs a time no later than now; ids are Torn's (word characters). The span is
 * clamped to now. The server can't check Torn's answer itself — what's sent is kept only for the signed-in player.
 */
export function cleanOwnUpload(stream: OwnStream, body: unknown, playerId: number, now: number): OwnUpload | { problem: string } {
  const b = body && typeof body === 'object' && !Array.isArray(body) ? (body as Record<string, unknown>) : null;
  if (!b) return { problem: 'Nothing sent.' };
  const raw = b.entries;
  const list: Array<[string, unknown]> = Array.isArray(raw)
    ? raw.map((e) => [String((e as Record<string, unknown> | null)?.id ?? ''), e] as [string, unknown])
    : raw && typeof raw === 'object'
      ? Object.entries(raw as Record<string, unknown>)
      : [];
  if (list.length > OWN_UPLOAD_MAX) return { problem: `At most ${OWN_UPLOAD_MAX} entries at once.` };
  // Torn's own answers for the player's own key, verbatim: anything else isn't taken at all.
  const entries: Record<string, Record<string, unknown>> = {};
  const times: number[] = [];
  for (const [id, e] of list) {
    if (!/^[\w-]{1,64}$/.test(id) || !e || typeof e !== 'object' || Array.isArray(e)) return { problem: 'Not Torn’s answer: an entry without an id.' };
    const r = e as Record<string, unknown>;
    if (!tornShaped(stream, r)) return { problem: `Not Torn’s answer: entry ${id} isn’t shaped like Torn’s ${stream}.` };
    const t = ownEntryTime(stream, r);
    if (t === null || t > now + 60_000) return { problem: `Not Torn’s answer: entry ${id} has no time, or one in the future.` };
    if (stream === 'attacks' && !ownFight(r, playerId)) return { problem: `Not your fight: entry ${id}.` };
    // An item snapshot: only its known fields are kept.
    entries[id] = stream === 'items' ? ((cleanItemSnap(r, Number.MAX_SAFE_INTEGER) ?? r) as unknown as Record<string, unknown>) : r;
    times.push(t);
  }
  let span: Span | null = null;
  const s = b.span && typeof b.span === 'object' ? (b.span as Record<string, unknown>) : null;
  if (s) {
    const from = Number(s.from);
    const to = Math.min(Number(s.to), now);
    if (Number.isFinite(from) && Number.isFinite(to) && from >= 0 && from <= to) span = spanForEntries({ from: Math.floor(from), to: Math.floor(to) }, times);
  }
  const cat = Number(b.cat);
  return { stream, cat: stream === 'log' && Number.isInteger(cat) && cat > 0 ? cat : null, entries, span };
}

/**
 * The stretch an upload may claim from the entries it carries (else none is recorded): it must reach the newest of them;
 * it starts at the oldest of them — or at the start (0) only for fewer than a page (Torn had nothing older); an upload
 * with no entries may only say "nothing older than this" (from 0).
 */
export function spanForEntries(span: Span, times: readonly number[], pageSize = 100): Span | null {
  if (!times.length) return span.from === 0 ? span : null;
  const low = Math.min(...times);
  const high = Math.max(...times);
  if (span.to < high - 1000) return null;
  if (span.from === 0) return times.length < pageSize ? span : { from: low, to: span.to };
  return { from: Math.max(span.from, low), to: span.to };
}

/**
 * The stretch a read of one page covered in full, given the cursor it read below (null = from now) and the times of what
 * came back: from the oldest entry up to the cursor — or from the start (0) when Torn gave nothing, or fewer than a page
 * below a cursor (a newest read with fewer than a page isn't trusted to be all: one more read below it settles it).
 */
export function pageSpan(cursor: number | null, times: readonly number[], pageSize: number, now: number): Span {
  const to = cursor ?? now;
  if (!times.length) return { from: 0, to };
  const low = Math.min(...times);
  return { from: times.length < pageSize && cursor !== null ? 0 : Math.min(low, to), to };
}

/** Where a backfill (or "read older") goes on from: below the newest unbroken stretch. null = nothing kept yet (read the newest); 0 = complete. */
export function backfillCursor(spans: readonly Span[]): number | null {
  const m = mergeSpans(spans);
  return m.length ? m[m.length - 1]!.from : null;
}

/** One stream's state, as `/api/me/own` reports it. */
export interface OwnStreamStatus {
  stream: OwnStream;
  count: number;
  /** Stretches known complete (ms), oldest first. */
  spans: Span[];
  /** Torn has nothing older than what's kept. */
  complete: boolean;
  oldest: number | null;
  newest: number | null;
  /** Bytes kept (Torn's JSON). */
  bytes: number;
  /** The server's walk back: waiting for you to be away, going, done, or not possible with your key. */
  backfill: 'waiting' | 'running' | 'done' | 'needs-key' | 'paused' | 'full';
}

export interface OwnStatus {
  /** Kept on the server (terms 19)? */
  on: boolean;
  streams: OwnStreamStatus[];
  bytes: number;
}
